/* eslint-disable no-useless-escape */
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import smoothscroll from 'smoothscroll-polyfill';

import AddToBag from '@/components/add_to_bag/add-to-bag.component';
import InfoTooltipSvgWhite from '@/components/common/InfoTooltipSvgWhite';
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverClose } from '@/components/common/popover';
import TooltipClose from '@/components/common/TooltipCloseSvg';
import Findmine from '@/components/findmine/Findmine';
import PdpEasyPayPopup from '@/components/pdp_easypay_popup/PdpEasyPayPopup';
import Truefit from '@/components/truefit_pdp/truefit-pdp.component';
import { ProductHelpers } from '@/components/v2/utils/ProductHelpers';
import SizeHelpers, { Size } from '@/components/v2/utils/SizeHelpers';
import { SwatchHelpers } from '@/components/v2/utils/SwatchHelpers';
import useBreakpoint from '@/components/v2/utils/useBreakPoint';
import { TmCart } from '@/tscapi/model/cart';
import { TmFavourite } from '@/tscapi/model/favourite';
import { TmSellerTypeEnum } from '@/tscapi/model/product';
import { TmCartLineRequest } from '@/tscapi/model/request/cart-request';
import { createCartAndAddItem, addItemToCart } from '@/tscapi/service/cart.service';
import http from '@/tscapi/service/httpservice';
import { Storage } from '@/tscapi/utils/browser-storage';
import { isConsentCategoryDisabled } from '@/tscapi/utils/consent';
import { Cookie } from '@/tscapi/utils/cookie';
import { FavouriteStorage } from '@/tscapi/utils/favourites-storage';

import RadioButtonOffSvg from '../common/RadioButtonOffSvg';
import RadioButtonOnSvg from '../common/RadioButtonOnSvg';
import { trackFBSearchEvent } from '../tracking_third_party/fbSearch';
import { trackTTSearchEvent } from '../tracking_third_party/tiktokSearch';

import FavoriteEmptyIcon from './FavoriteEmptyIcon';
import FavoriteIcon from './FavoriteIcon';
import { en, fr } from './i18n';
import { IProduct, IProductEdp } from './IProduct';
import PdpAdvanceOrder from './PdpAdvanceOrder';
import PdpEasyPayButton from './PdpEasyPayButton';
import EventBus from './PdpEventBus';
import PdpItemNo from './PdpFreeShippingItemNo';
import PdpName from './PdpName';
import PdpPrices from './PdpPrice';
import PdpSticky from './PdpSticky';
import { handleSearchTracking } from './utils/sessionStorage';
import { handleAlgoliaParameters } from './utils/urlParameters';

export interface PdpProps {
    productData: IProduct;
}
export interface EasyPayData {
    title: string;
    content: string;
}
declare let pwr: any;
declare global {
    interface Window {
        pwr: any;
    }
}

type FavoriteEventDetail = {
    productData: IProduct;
    selectedEdp: number;
};

// Easiest way to declare a Function Component; return type is inferred.
const PdpDescriptionArea = ({ productData }: PdpProps) => {
    const {
        DefaultEdp,
        IsSoldOut,
        Styles = [],
        Sizes = [],
        AvailableSwatches,
        Edps,
        DeliveryOptions,
        ProductTypePath,
        SellerName,
        SellerPageLink,
        SellerType,
    } = productData;
    const hasFBTrackingFired = useRef(false);

    const { i18n, t } = useTranslation('pdp-description');
    i18n.addResourceBundle('en', 'pdp-description', en, true, true);
    i18n.addResourceBundle('fr', 'pdp-description', fr, true, true);

    const FAVORITE_TOOLTIP_CLOSE_TIMEOUT = 3000;
    const IMAGE_URL_PREFIX = 'https://item.tscimg.ca/TSC/';
    const LOADING_ASSET_PATH = '/App_Assets/public/loading.gif';
    const referenceRef = useRef(null);

    const { isMobile } = useBreakpoint();

    const isSignedIn = useMemo(() => {
        const cookie = new Cookie();
        return !!cookie.UserAccessToken;
    }, []);

    const styles = useMemo(() => {
        return !Styles ? [] : Styles;
    }, [Styles]);

    const sizes = useMemo(() => {
        return !Sizes ? [] : Sizes;
    }, [Sizes]);

    const edps = useMemo(() => {
        return !Edps ? [] : Edps;
    }, [Edps]);

    const onFavoriteTooltipCloseClick = (
        e:
            | React.MouseEvent<HTMLButtonElement, MouseEvent>
            | React.MouseEvent<HTMLDivElement, MouseEvent>
            | React.KeyboardEvent<HTMLDivElement>,
    ) => {
        e.preventDefault?.();
        setShowFavoriteTooltip(false);
    };
    const getDefaultEdpItem = useMemo(() => {
        return edps.find((edpItem) => edpItem.EdpNo === DefaultEdp);
    }, [edps, DefaultEdp]);
    const [selectedStyleIndex, setSelectedStyleIndex] = useState(
        !Styles ? -1 : Styles.findIndex((v) => v.DimensionId === getDefaultEdpItem.StyleDimensionId),
    );
    const [selectedSizeIndex, setSelectedSizeIndex] = useState(
        !Sizes ? -1 : Sizes.findIndex((v) => v.DimensionId === getDefaultEdpItem.SizeDimensionId),
    );
    const isAutoDeliveryEnabled = useMemo(() => {
        //setReferenceElement(document.getElementById('adpPopup') as HTMLElement);
        return DeliveryOptions && DeliveryOptions.length > 0;
    }, [DeliveryOptions]);

    const [isFavourite, setIsFavourite] = useState(false);
    const [favourites, setFavourites] = useState([]);
    const [currentIsFavorite, setCurrentIsFavorite] = useState(/*isFavorite*/ false);
    const [showFavoriteTooltip, setShowFavoriteTooltip] = useState(false);
    const [showEasyPayPopup, setShowEasyPayPopup] = useState(false);
    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const [selectedADP, setSelectedADP] = useState<number>();
    const [isAddedToBag, setIsAddedToBag] = useState(false);
    const [gotoIndex, setGotoIndex] = useState(0);
    const [popupOpen, setPopupOpen] = useState(false);
    const [isAddToBagLoading, setIsAddToBagLoading] = useState(false);
    const [easyPayPopupData, setEasyPayPopupData] = useState<EasyPayData>({ title: '', content: '' });
    const [isSwatch, setIsSwatch] = useState(false);
    const [labelSize, setLabelSize] = useState<Size>('md');
    const isInitialMount = useRef(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [frequency, setFrequency] = useState(null);
    const cookie: Cookie = new Cookie();

    //useEffect(()=>{},[])

    const getCurrentEdpItem = useMemo(() => {
        let currentEdp: IProductEdp | undefined = undefined;
        if (sizes.length === 0 && styles.length === 0) {
            //has no styles or sizes
            if (isAutoDeliveryEnabled) {
                currentEdp = currentEdp = selectedADP
                    ? edps.find((edpItem) => {
                          return edpItem.EdpNo === selectedADP;
                      })
                    : getDefaultEdpItem;
            } else {
                const tmp = getDefaultEdpItem;
                currentEdp = tmp;
            }
        } else if (sizes.length > 0 && styles.length === 0) {
            // has sizes no styles
            if (selectedSizeIndex !== -1) {
                currentEdp = edps.find((edpItem) => {
                    return edpItem.SizeDimensionId === sizes[selectedSizeIndex].DimensionId;
                });
            } else {
                const tmp = getDefaultEdpItem;
                currentEdp = tmp && tmp.Inventory > 0 && tmp.Inventory < 10 ? tmp : undefined;
            }
        } else if (styles.length > 0 && sizes.length === 0) {
            // has styles no size
            if (selectedStyleIndex !== -1) {
                currentEdp = edps.find((edpItem) => {
                    return edpItem.StyleDimensionId === styles[selectedStyleIndex].DimensionId;
                });
            } else {
                const tmp = getDefaultEdpItem;
                currentEdp = tmp && tmp.Inventory > 0 && tmp.Inventory < 10 ? tmp : undefined;
            }
        } else if (selectedSizeIndex === -1 && selectedStyleIndex === -1) {
            // has both Style/Size but not selected
            const tmp = getDefaultEdpItem;
            currentEdp = tmp;
        } else if (selectedSizeIndex !== -1 && selectedStyleIndex !== -1) {
            // has both Style/Size and both selected
            currentEdp = edps.find((edpItem) => {
                return (
                    edpItem.SizeDimensionId === sizes[selectedSizeIndex].DimensionId &&
                    edpItem.StyleDimensionId === styles[selectedStyleIndex].DimensionId
                );
            });
        }
        //set default Standing Order selection
        if (currentEdp.IsSOEligible) {
            setFrequency('AUTO DELIVERY');
        } else {
            setFrequency('1 TIME SHIPMENT');
        }
        return currentEdp;
    }, [
        edps,
        getDefaultEdpItem,
        selectedSizeIndex,
        selectedStyleIndex,
        sizes,
        styles,
        selectedADP,
        isAutoDeliveryEnabled,
    ]);

    useEffect(() => {
        // fire event to trigger FB viewContent event with productData and currentEdpItem
        if (!hasFBTrackingFired.current) {
            document.dispatchEvent(
                new CustomEvent('pdpEvent', { detail: { productData, selectedEdp: getCurrentEdpItem } }),
            );

            // Trigger TikTok view content event
            document.dispatchEvent(
                new CustomEvent('viewContentEvent', {
                    detail: {
                        productData,
                        selectedEdp: getCurrentEdpItem.EdpNo,
                    },
                }),
            );

            hasFBTrackingFired.current = true;
        }
    }, [getCurrentEdpItem, productData]);

    useEffect(() => {
        handleSearchTracking(trackFBSearchEvent, trackTTSearchEvent);
        handleAlgoliaParameters(productData.ItemNo);
    }, []);

    function scrolToFn() {
        //document.querySelector<HTMLElement>("[data-text='size chart']").scrollIntoView();
        let stickyOffset = 20;
        if (window.innerWidth > 768) {
            stickyOffset = 120;
        }
        const topOfElement = document.querySelector<HTMLElement>("[data-text='size chart']").offsetTop - stickyOffset;
        window.scroll({ top: topOfElement, behavior: 'smooth' });
        const sizeChartAccordion = document.querySelector<HTMLElement>("[data-text='size chart']");
        //open the accordion if not already open
        if (sizeChartAccordion && !sizeChartAccordion.classList.contains('open')) {
            sizeChartAccordion.click();
        }
    }
    const renderReviewSnippet = useCallback(() => {

    }, [productData.ItemNo]);

    const gotoSlide = useCallback(
        (styleIndex: number) => {
            EventBus.dispatch('gotoSlide', { dimensionId: styles[styleIndex].DimensionId });
        },
        [styles],
    );

    const isPermanentlyOutOfStock = useMemo(() => {
        if (IsSoldOut === null || IsSoldOut === undefined) {
            return false;
        }
        return IsSoldOut;
    }, [IsSoldOut]);

    const isOutOfStock = useMemo(() => {
        return getCurrentEdpItem.Inventory <= 0;
    }, [getCurrentEdpItem]);

    const isCriticStock = useMemo(() => {
        const currentEdp = getCurrentEdpItem;
        if (!currentEdp || !(currentEdp.Inventory > 0)) {
            return false;
        }
        return currentEdp.Inventory < 10 && currentEdp.Inventory > 0;
    }, [getCurrentEdpItem]);

    const critickStockText = useMemo(() => {
        const currentEdp = getCurrentEdpItem;

        if (!currentEdp || !(currentEdp.Inventory > 0)) {
            return '';
        }
        return t('only') + currentEdp.Inventory + t('left');
    }, [getCurrentEdpItem, t]);

    const allowedQuantites = useMemo(() => {
        let edp: IProductEdp | null = null;
        if (sizes.length === 0 && styles.length === 0) {
            edp = edps[0];
        } else {
            edp = getCurrentEdpItem;
        }
        if (!edp || !(edp.Inventory > 0)) {
            return [0];
        }
        const qty: number[] = [];
        const qtyLimit = Math.min(edp.Inventory, 10);

        for (let i = 1; i <= qtyLimit; i++) {
            qty.push(i);
        }

        return qty;
    }, [getCurrentEdpItem, sizes, styles, edps]);

    const buttonText = useMemo(() => {
        if (isAddedToBag) {
            return t('addedToBag');
        }
        if (isPermanentlyOutOfStock) {
            return t('permanentlyOutOfStockButtonText');
        }
        if (isOutOfStock) {
            // return t('emailWhenInStockText');
            return t('outOfStockText');
        }
        if (styles.length === 0 && sizes.length === 0) {
            return t('addToBagText');
        }
        if (styles.length === 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1) {
                return t('selectSize');
            }
        }
        if (styles.length !== 0 && sizes.length === 0) {
            if (selectedStyleIndex === -1) {
                return t('selectStyle');
            }
        }
        if (styles.length !== 0 && sizes.length !== 0) {
            if (selectedSizeIndex === -1 && selectedStyleIndex === -1) {
                return t('selectSizeAndStyle');
            }

            if (selectedSizeIndex !== -1 && selectedStyleIndex === -1) {
                return t('selectStyle');
            }

            if (selectedSizeIndex === -1 && selectedStyleIndex !== -1) {
                return t('selectSize');
            }
        }
        return t('addToBagText');
    }, [isOutOfStock, selectedSizeIndex, selectedStyleIndex, styles, sizes, isAddedToBag, isPermanentlyOutOfStock, t]);

    const isCtaDisabled = useMemo(() => {
        if (isPermanentlyOutOfStock || isOutOfStock) {
            return true;
        }

        if (buttonText === t('addToBagText') || buttonText === t('addedToBag')) {
            return false;
        }

        return true;
    }, [isPermanentlyOutOfStock, isOutOfStock, t, buttonText]);

    function setURLParameter(url, parameter, value) {
        const urlTemp = new URL(url);
        if (urlTemp.searchParams.get(parameter) === value) {
            return url;
        }
        urlTemp.searchParams.set(parameter, value);
        return decodeURIComponent(urlTemp.href);
    }

    useEffect(() => {
        function updateUrlEdp() {
            //prevent udpating URL if only changing edp for Autodelivery

            // update the edp param in url  e.g ?nav=R:582519&edp=11786296
            const selectedEdpData = getCurrentEdpItem;
            const selectedEDPNum = selectedEdpData
                ? selectedEdpData.EdpNo
                : getDefaultEdpItem
                  ? getDefaultEdpItem.EdpNo
                  : 0;
            const newURL = setURLParameter(window.location.href, 'edp', selectedEDPNum);
            window.history.replaceState({}, '', newURL);
        }
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            // updated url with EDP Param if not initial mount of page
            updateUrlEdp();
        }
        const tmpEdpData = getCurrentEdpItem;
        window.analyticsData.page.selectedEdp = tmpEdpData.EdpNo;
        window.analyticsData.page.selectedEdpData = tmpEdpData;
    }, [
        selectedSizeIndex,
        selectedStyleIndex,
        isAutoDeliveryEnabled,
        selectedADP,
        getCurrentEdpItem,
        getDefaultEdpItem,
    ]);

    const isAnySOEligible = useMemo(() => {
        let isAnySOEligible = false;
        for (const edp of edps) {
            if (edp.IsSOEligible == true) {
                isAnySOEligible = true;
                break;
            }
        }
        return isAnySOEligible;
    }, [edps]);

    const isFrequencyAutoDelivery = useMemo(() => {
        let isFrequencyAutoDelivery = false;

        if (getCurrentEdpItem.IsSOEligible == true) {
            isFrequencyAutoDelivery = true;
        }
        return isFrequencyAutoDelivery;
    }, [getCurrentEdpItem]);

    const availableSwatches = useMemo(() => {
        return !AvailableSwatches ? [] : AvailableSwatches;
    }, [AvailableSwatches]);

    useEffect(() => {
        // update selected edp based on event from Gallery
        setSelectedStyleIndex(gotoIndex);
    }, [gotoIndex]);

    const processRecentlyViewed = useCallback(() => {
        if (productData != undefined) {
            const cookie: Cookie = new Cookie();
            let rvItemNos: string[] = [];
            const itemNo = productData.ItemNo;

            if (cookie.RecentlyViewedItemNos != undefined) {
                rvItemNos = cookie.RecentlyViewedItemNos.split('-');
            }

            if (itemNo != undefined && itemNo.length === 6) {
                // Remove if already exists
                rvItemNos = rvItemNos.filter((no) => no !== itemNo);
                // Add to front
                rvItemNos.unshift(itemNo);
                // Keep only 10 most recent
                if (rvItemNos.length > 10) {
                    rvItemNos = rvItemNos.slice(0, 10);
                }
                cookie.RecentlyViewedItemNos = rvItemNos.join('-');
            }
        }
    }, [productData]);

    const fetchEasyPay = async () => {
        async function fetchArticle() {
            const headers = {
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
                currentdatetime: new Cookie().CurrentDate,
            };
            const base = '/api/v2/';
            const lang = new Cookie().Language.substring(0, 2) || 'en';
            const easyPayUrl = base + lang + '/customerservice/easy-pay';
            try {
                const data = await http.get(easyPayUrl, {
                    headers: headers,
                });
                return data.data.Article;
            } catch (e) {
                return null;
            }
        }
        //if content is null, fetch easy pay data
        if (easyPayPopupData.content == '') {
            const article = await fetchArticle();
            if (article) {
                setEasyPayPopupData({ title: article.Title, content: article.Content });
                setShowEasyPayPopup(true);
            }
        } else {
            setShowEasyPayPopup(true);
        }
    };

    const getUserFavourites = useCallback(() => {
        const favouriteStorage = new FavouriteStorage();
        const favList = favouriteStorage.Favourites;
        const foundFav = favList.find((item) => item.ItemNumber == productData.ItemNo);
        setFavourites(favouriteStorage.Favourites);
        if (foundFav) {
            setIsFavourite(true);
        }
    }, [productData.ItemNo]);

    const handleSignInLink = (e) => {
        e.preventDefault();
        cookie.TargetUrl = window.location.pathname + window.location.search;
        window.location.href = '/pages/signin';
    };

    const onFavoriteClick = async () => {
        if (!isSignedIn) {
            setShowFavoriteTooltip(true);
            // setTimeout(() => {
            //     setShowFavoriteTooltip(false);
            // }, FAVORITE_TOOLTIP_CLOSE_TIMEOUT);
            return;
        }

        const uat = new Cookie().UserAccessToken;

        if (!uat) {
            setShowFavoriteTooltip(true);
            setTimeout(() => {
                setShowFavoriteTooltip(false);
            }, FAVORITE_TOOLTIP_CLOSE_TIMEOUT);
            return;
        }

        const userEdp = uat.Edp;

        if (!userEdp) {
            setShowFavoriteTooltip(true);
            setTimeout(() => {
                setShowFavoriteTooltip(false);
            }, FAVORITE_TOOLTIP_CLOSE_TIMEOUT);
            return;
        }

        const base = '/api/v2/';
        const lang = new Cookie().Language.substring(0, 2) || 'en';
        const item = getCurrentEdpItem;
        const deleteUrl = base + lang + '/accounts' + `/${userEdp}` + `/favourites/${item.ItemNo}`;

        const cachebleHeaders = {
            'Content-Type': 'application/json',
            'cache-control': 'no-cache',
            currentdatetime: new Cookie().CurrentDate,
        };
        const favouriteStorage = new FavouriteStorage();
        if (isFavourite) {
            try {
                await http.delete(deleteUrl, {
                    headers: cachebleHeaders,
                });
                favouriteStorage.removeByItemNumber(item.ItemNo);
                setIsFavourite(false);
            } catch (e) {
                console.log(e);
            }
        } else {
            try {
                const postUrl = base + lang + '/accounts' + `/${userEdp}` + '/favourites';
                await http.post(
                    postUrl,
                    {
                        ItemNumber: item.ItemNo,
                        ActivityDateTime: new Date().toISOString(),
                    },
                    {
                        headers: cachebleHeaders,
                    },
                );
                const favourite: TmFavourite = new TmFavourite();
                favourite.ItemNumber = item.ItemNo;
                favouriteStorage.add(favourite);
                setIsFavourite(true);
                // trigger analytics event
                const event = new CustomEvent<FavoriteEventDetail>('favoriteEvent', {
                    detail: { productData, selectedEdp: getCurrentEdpItem.EdpNo },
                });
                // Dispatch the event
                document.dispatchEvent(event);
            } catch (e) {
                console.log(e);
            }
        }
        setCurrentIsFavorite((prev) => !prev);
        return;
    };

    const cartSuccess = (cartData: TmCart) => {
        const cookie: Cookie = new Cookie();
        //save cart to RQ cache
        queryClient.setQueryData(
            ['bag', cartData?.CartGuid, cookie.UserAccessToken?.Edp, cookie.Language],
            cartData as TmCart,
        );

        cookie.CartGuid = cartData.CartGuid;
        cookie.CartProductCount = cartData.OrderSummary.TotalQuantity.toString();
        const item = getCurrentEdpItem;

        const storage: Storage = new Storage();
        storage.setSessionCart(cartData);

        window.analyticsData.cart = cartData;
        // trigger analytics event

        setIsAddedToBag(true);
        setTimeout(() => {
            setIsAddedToBag(false);
        }, 3000);
        setIsAddToBagLoading(false);

        EventBus.dispatch('change', { edp: item.EdpNo, qty: selectedQuantity });

        window.analyticsData.page.pageTitle = 'add to cart interstitial';
        window.analyticsData.page.pageType = 'add to cart interstitial';
        window.analyticsData.event.eventTitle = 'add-to-cart';

        const elem = document.getElementById('btnAddToCart');
        const event = new Event('analytics_AddToCart');
        elem.dispatchEvent(event);
    };
    const changeToSize = useCallback(
        (e) => {
            if (e?.detail?.size) {
                const sizeIndex = sizes.findIndex((x) => {
                    return x.DisplayName == e.detail.size;
                });
                if (!ProductHelpers.isSizeDisabledV2(sizeIndex, selectedStyleIndex, edps, styles, sizes)) {
                    //recommended size is available, select that size
                    setSelectedSizeIndex(sizeIndex);
                }
            }
        },
        [edps, selectedStyleIndex, sizes, styles],
    );
    const listenForTruefitEvent = useCallback(() => {
        document.addEventListener(
            'tf-sizechanage',
            function (e: CustomEvent) {
                changeToSize(e);
            }.bind(this),
            false,
        );
    }, []);

    useEffect(() => {
        document.addEventListener(
            'tf-sizechanage',
            function (e: CustomEvent) {
                changeToSize(e);
            }.bind(this),
            false,
        );
    }, [changeToSize]);

    const cartError = (errorData) => {
        //window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
        window.scrollTo(0, 0);
        setIsAddedToBag(false);
        setIsAddToBagLoading(false);
        setErrorMessage(errorData);
        //console.log(`Add To Cart Error: ${errorData}`);
    };

    const addToBagAction = async (sizeIndex: number, styleIndex: number) => {
        setIsAddToBagLoading(true);
        const lang = new Cookie().Language.substring(0, 2) || 'en';
        const cookie = new Cookie();

        const selectedProduct = getCurrentEdpItem;
        if (!selectedProduct) {
            setIsAddToBagLoading(false);
            return false;
        }

        const cartlineModel: TmCartLineRequest = new TmCartLineRequest();
        cartlineModel.ItemEDP = selectedProduct.EdpNo;

        if (!inView && entry?.boundingClientRect.top < 0) {
            cartlineModel.Quantity = 1;
        } else {
            cartlineModel.Quantity = selectedQuantity;
        }

        if (isAnySOEligible) {
            if (frequency == 'AUTO DELIVERY') {
                cartlineModel.SOInterval = selectedProduct.SODefaultIntervalValue;
                cartlineModel.SOPeriod = selectedProduct.SOIntervalType;
            }
        }

        cartlineModel.Customization = '';
        if (cookie.UserAccessToken != null && cookie.UserAccessToken.Edp != null) {
            cartlineModel.CustomerEdp = cookie.UserAccessToken.Edp;
        }
        if (cookie != undefined && cookie.CartGuid != undefined) {
            try {
                const result = await addItemToCart(cartlineModel);
                // window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
                //window.scrollTo(0, 0);
                cartSuccess(result.data as TmCart);
            } catch (errorData) {
                cartError(errorData);
            }
        } else {
            try {
                const result = await createCartAndAddItem(cartlineModel);
                // window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
                //window.scrollTo(0, 0);
                cartSuccess(result.data as TmCart);
            } catch (errorData) {
                cartError(errorData);
            }
        }
    };

    const onAddToBagClick = () => {
        if (isAddToBagLoading || isAddedToBag) {
            return;
        }
        setIsAddToBagLoading(true);
        addToBagAction(selectedSizeIndex, selectedStyleIndex);
    };

    const loadWriteReview = useCallback(() => {
        const queryString = window.location.search;
        const queryParams = new URLSearchParams(queryString);

        if (queryParams != undefined) {
            const loadReviewFlag = queryParams.get('openreview') as string;
            if (loadReviewFlag != undefined && loadReviewFlag.toLowerCase().startsWith('true')) {
                const userEmail = queryParams.get('pr_merchant_user_email') as string;
                const source = queryParams.get('pr_source') as string;
                const cid = queryParams.get('et_cid') as string;
                const rid = queryParams.get('et_rid') as string;

                let qParams = '';
                if (userEmail != undefined) {
                    qParams = '&pr_merchant_user_email=' + userEmail;
                }
                if (source != undefined) {
                    qParams = qParams + '&pr_source=' + source;
                }
                if (cid != undefined) {
                    qParams = qParams + '&et_cid=' + cid;
                }
                if (rid != undefined) {
                    qParams = qParams + '&et_rid=' + rid;
                }

                window.location.href = '/pages/writereview?pr_page_id=' + productData.ItemNo + qParams;
            }
        }
    }, [productData.ItemNo]);

    useEffect(() => {
        loadWriteReview();
        smoothscroll.polyfill();
        const findIndexByStyleID = (styleID) => {
            const styleIndeFound = styles.findIndex((style) => {
                return style.DimensionId == styleID;
            });
            if (styleIndeFound != -1) {
                setSelectedStyleIndex(styleIndeFound);
                gotoSlide(styleIndeFound);
                //setGotoIndex(0);
            }
        };
        const item = getDefaultEdpItem;
        const styleIndex = styles.findIndex((v) => v.DimensionId === item.StyleDimensionId);
        const sizeIndex = sizes.findIndex((v) => v.DimensionId === item.SizeDimensionId);
        setSelectedStyleIndex(styleIndex);
        setSelectedSizeIndex(sizeIndex);
        renderReviewSnippet();
        getUserFavourites();
        if (isAutoDeliveryEnabled) {
            setSelectedADP(item.EdpNo);
        }
        document.addEventListener('mousedown', handleDocumentClick);
        //listenForTFEvenet();
        processRecentlyViewed();
        //setup event bus handler to look for msgs from PDP Gallery
        EventBus.on('gotoStyle', (data) => {
            findIndexByStyleID(data.dimensionId);
        });
        return () => {
            EventBus.remove('gotoStyle', (data) => {
                findIndexByStyleID(data.dimensionId);
            });
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [
        getDefaultEdpItem,
        getUserFavourites,
        gotoSlide,
        isAutoDeliveryEnabled,
        loadWriteReview,
        processRecentlyViewed,
        renderReviewSnippet,
        sizes,
        styles,
    ]);

    useEffect(() => {
        //initially we checked if all swatches exist but for optimizatin we only now check one
        //if one exists, we assume all exist
        SwatchHelpers.doSwatchesExist(AvailableSwatches)
            .then((result) => {
                setIsSwatch(result);
            })
            .catch(() => {
                setIsSwatch(false);
            });
    }, [setIsSwatch, AvailableSwatches]);

    const showOptions = useMemo(() => {
        return !(sizes.length === 0 && styles.length === 0);
    }, [styles, sizes]);

    const isSizeDisabled = useCallback(
        (i: number) => {
            return ProductHelpers.isSizeDisabledV2(i, selectedStyleIndex, edps, styles, sizes);
        },
        [selectedStyleIndex, edps, styles, sizes],
    );

    const isStyleDisabled = useCallback(
        (i: number) => {
            return ProductHelpers.isStyleDisabledV2(i, selectedSizeIndex, edps, styles, sizes);
        },
        [selectedSizeIndex, edps, styles, sizes],
    );

    const firstAvailableStyleIndex = useMemo(() => {
        for (let i = 0; i < styles.length; i++) {
            if (!isStyleDisabled(i)) {
                return i;
            }
        }
        return -1;
    }, [styles, isStyleDisabled]);

    const onSelectSizeClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
        e.preventDefault();
        //do check if selected style/size combination exists
        //if size doesnt exist, move to next available size.
        //what if there is no available size?
        //
        setSelectedSizeIndex(index);
    };

    function onFrequencyChange(event) {
        setFrequency(event.target.value);
    }

    function handleDocumentClick(event) {
        if (referenceRef?.current) {
            if (referenceRef.current.contains(event.target)) {
                return;
            }
            setPopupOpen(false);
        }
    }

    const renderStyleSwatch = useCallback(() => {
        const onSelectColorClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
            e.preventDefault();
            gotoSlide(index);
            setSelectedStyleIndex(index);
            //updateUrlEdp();

            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        };
        const getSwatchLink = (index: number) => {
            const resource = availableSwatches[index];
            if (!resource) {
                setSelectedStyleIndex;
                return '';
            }
            return IMAGE_URL_PREFIX + resource;
        };
        if (!isSwatch) {
            return <></>;
        }

        return styles.map((style, index) => (
            <button
                key={style.DisplayName}
                className={classNames('pdp-description__form__colours__selections__option', {
                    'pdp-description__form__colours__selections__option--selected': index === selectedStyleIndex,
                    'pdp-description__form__colours--disabled': isStyleDisabled(index),
                })}
                onClick={(e) => onSelectColorClick(e, index)}
                //disabled={isStyleDisabled(index)}
                aria-pressed={index === selectedStyleIndex}
            >
                <input
                    type="checkbox"
                    id={style.DisplayName || ''}
                    checked={index === selectedStyleIndex}
                    //disabled={isStyleDisabled(index)}
                    onChange={() => setSelectedStyleIndex(index)}
                />
                <label
                    className="pdp-description__form__colours__selections__option-label"
                    htmlFor={style.DisplayName || ''}
                    style={{ backgroundImage: `url(${getSwatchLink(index)})` }}
                >
                    <span className="visually-hidden">{style.DisplayName}</span>
                </label>
            </button>
        ));
    }, [isSwatch, styles, isStyleDisabled, selectedStyleIndex, setSelectedStyleIndex, availableSwatches, gotoSlide]);

    const renderStyleDropdown = useCallback(() => {
        if (styles.length === 0) {
            return <></>;
        }

        return (
            <select
                className="pdp-description__form__colours__selections__dropdown rounded-none"
                onChange={(e) => {
                    const index = styles.findIndex((style) => {
                        return style.DimensionId === e.target.value;
                    });
                    setSelectedStyleIndex(index);
                    gotoSlide(index);
                }}
                value={styles[selectedStyleIndex]?.DimensionId}
            >
                {styles.map((style, styleIndex) => (
                    <option
                        key={style.DimensionId}
                        value={style.DimensionId}
                        className={isStyleDisabled(styleIndex) ? 'disabled-option-style' : ''}
                        disabled={isStyleDisabled(styleIndex)}
                    >
                        {style.DisplayName}
                    </option>
                ))}
            </select>
        );
    }, [styles, setSelectedStyleIndex, selectedStyleIndex, isStyleDisabled, gotoSlide]);

    const { ref, inView, entry } = useInView({
        rootMargin: '0px',
    });

    const queryClient = useQueryClient();

    return (
        <>
            {getCurrentEdpItem != undefined && (
                <>
                    <PdpName
                        ProductData={productData}
                        SelectedEdpItem={getCurrentEdpItem}
                        isAutoDeliveryEnabled={isAutoDeliveryEnabled}
                        Selector="lblProductName"
                    />

                    <PdpItemNo
                        ProductData={productData}
                        SelectedEdpItem={getCurrentEdpItem}
                        isAutoDeliveryEnabled={isAutoDeliveryEnabled}
                    />

                    <PdpPrices ProductData={productData} SelectedEdpItem={getCurrentEdpItem} Selector="pricesPortal" />

                    <Findmine productData={productData} SelectedEdpItem={getCurrentEdpItem} />

                    <PdpEasyPayButton
                        ProductData={productData}
                        SelectedEdpItem={getCurrentEdpItem}
                        getEasyPayArticle={fetchEasyPay}
                        isAutoDeliveryEnabled={isAutoDeliveryEnabled}
                    />
                    <form className="pdp-description__form">
                        {sizes.length !== 0 && (
                            <>
                                <fieldset className="pdp-description__form__sizes">
                                    <legend className="visually-hidden">{t('sizes')}</legend>

                                    <div className="pdp-description__form__sizes__selected">
                                        {t('size')}:{' '}
                                        {selectedSizeIndex !== -1 ? <b>{sizes[selectedSizeIndex]?.DisplayName}</b> : ''}
                                    </div>
                                    <div
                                        className={classNames('pdp-description__form__sizes__selections', {
                                            'pdp-description__form__sizes__selections__items--sm': labelSize === 'sm',
                                            'pdp-description__form__sizes__selections__items--md': labelSize === 'md',
                                            'pdp-description__form__sizes__selections__items--lg': labelSize === 'lg',
                                            'pdp-description__form__sizes__selections__items--xl': labelSize === 'xl',
                                        })}
                                    >
                                        {sizes.map((size, index) => (
                                            <button
                                                key={size.DisplayName}
                                                className={classNames(
                                                    'pdp-description__form__sizes__selections__option',
                                                    {
                                                        'pdp-description__form__sizes__selections__option--selected':
                                                            index === selectedSizeIndex,
                                                        'pdp-description__form__sizes--disabled': isSizeDisabled(index),
                                                    },
                                                )}
                                                onClick={(e) => onSelectSizeClick(e, index)}
                                                type={'button'}
                                                aria-pressed={index === selectedSizeIndex}
                                            >
                                                <input
                                                    type="radio"
                                                    id={size.DisplayName || ''}
                                                    checked={index === selectedSizeIndex}
                                                    onChange={() => setSelectedSizeIndex(index)}
                                                />
                                                <label
                                                    className="pdp-description__form__sizes__option cursor-pointer"
                                                    htmlFor={size.DisplayName || ''}
                                                >
                                                    <span className="visually-hidden">{t('size')}</span>
                                                    {size.DisplayName || ''}
                                                </label>
                                            </button>
                                        ))}
                                    </div>
                                </fieldset>
                                <div className="pdp-description__form__size-chart-wrapper">
                                    {/* prevent truefit if functional consent disabled */}
                                    {!isConsentCategoryDisabled('functional') && (
                                        <Truefit ProductData={productData} SelectedEdpItem={getCurrentEdpItem} />
                                    )}
                                    {!!window['sizeChartTab'] && (
                                        <button
                                            className="pdp-description__form__size-chart-link"
                                            type="button"
                                            onClick={(e) => {
                                                scrolToFn();
                                            }}
                                        >
                                            Sizing Chart
                                        </button>
                                    )}
                                </div>
                            </>
                        )}

                        {styles?.length !== 0 && (
                            <>
                                <fieldset className="pdp-description__form__colours">
                                    <legend className="visually-hidden">{t('colours')}</legend>

                                    <div className="pdp-description__form__colours__selected">
                                        {t('style')}:{' '}
                                        <span className="pdp-description__form__colours__selected__color">
                                            {selectedStyleIndex !== -1 ? styles[selectedStyleIndex].DisplayName : ''}
                                        </span>
                                    </div>

                                    <div className="pdp-description__form__colours__selections">
                                        {isSwatch ? renderStyleSwatch() : renderStyleDropdown()}
                                    </div>
                                </fieldset>
                                <div className="horizontal-divider pdp-description__divider" />
                            </>
                        )}

                        {isAnySOEligible && (
                            <>
                                <fieldset className="frequency">
                                    <div className="frequency__selected">
                                        {t('Frequency')} : <b>{frequency}</b>
                                    </div>
                                    <ul>
                                        <li className={!isFrequencyAutoDelivery ? 'frequency__disabled' : ''}>
                                            <label htmlFor="autoDelivery">
                                                <input
                                                    type="radio"
                                                    className="frequency__input"
                                                    id="autoDelivery"
                                                    name="frequencySelection"
                                                    style={{ display: 'none' }}
                                                    value="AUTO DELIVERY"
                                                    disabled={!isFrequencyAutoDelivery}
                                                    checked={frequency === 'AUTO DELIVERY'}
                                                    onChange={(e) => {
                                                        onFrequencyChange(e);
                                                    }}
                                                />
                                                {frequency === 'AUTO DELIVERY' ? (
                                                    <RadioButtonOnSvg />
                                                ) : (
                                                    <RadioButtonOffSvg />
                                                )}
                                                <div className="frequency__description">
                                                    <div className="frequency__first-row">
                                                        <span style={{ marginRight: '7px' }}>
                                                            {t('Auto Delivery')}:
                                                        </span>
                                                        <Popover
                                                            placement={isMobile ? 'top' : 'right-start'}
                                                            autoUpdateEnabled={true}
                                                        >
                                                            <PopoverTrigger className="popover__button">
                                                                <span className="visually-hidden">
                                                                    {t('Delivery Information')}
                                                                </span>
                                                                <InfoTooltipSvgWhite className="popover__tooltip" />
                                                            </PopoverTrigger>
                                                            <PopoverContent className="popover__message pdp-description__form__auto-delivery__popup">
                                                                Love this product? Sign up for our Auto-Delivery
                                                                Program!{' '}
                                                                <ul className="dashed">
                                                                    <li>Lock in the price</li>
                                                                    <li>Get it on a schedule</li>
                                                                    <li>Cancel at any time without risk</li>
                                                                </ul>
                                                                <PopoverClose />
                                                                <PopoverArrow className="popover__arrow" />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </div>
                                                    {isFrequencyAutoDelivery && (
                                                        <div className="frequency__second-row">
                                                            Receive this item every{' '}
                                                            {getCurrentEdpItem.SODefaultIntervalValue} days
                                                        </div>
                                                    )}
                                                    {!isFrequencyAutoDelivery && (
                                                        <div className="frequency__second-row">&nbsp;</div>
                                                    )}
                                                </div>
                                            </label>
                                        </li>

                                        <li className="frequency__selected__listitem">
                                            <label htmlFor="oneTimeShipment">
                                                <input
                                                    type="radio"
                                                    className="frequency__input"
                                                    id="oneTimeShipment"
                                                    name="frequencySelection"
                                                    value="1 TIME SHIPMENT"
                                                    style={{ display: 'none' }}
                                                    checked={frequency === '1 TIME SHIPMENT'}
                                                    onChange={(e) => {
                                                        onFrequencyChange(e);
                                                    }}
                                                />
                                                {frequency === '1 TIME SHIPMENT' ? (
                                                    <RadioButtonOnSvg />
                                                ) : (
                                                    <RadioButtonOffSvg />
                                                )}
                                                <div className="frequency__description"> {t('oneTimeShipment')}</div>
                                            </label>
                                        </li>
                                    </ul>
                                </fieldset>
                                <div className="horizontal-divider pdp-description__divider" />
                            </>
                        )}

                        {isAutoDeliveryEnabled && (
                            <>
                                <fieldset className="pdp-description__form__auto-delivery">
                                    <legend className="visually-hidden">Auto delivery</legend>

                                    <div className="pdp-description__form__auto-delivery__selections">
                                        <b style={{ display: 'flex' }}>
                                            Delivery Option:
                                            <Popover placement="top">
                                                <PopoverTrigger className="popover__button">
                                                    <span className="visually-hidden">{t('Delivery Information')}</span>
                                                    <InfoTooltipSvgWhite className="popover__tooltip" />
                                                </PopoverTrigger>
                                                <PopoverContent className="popover__message pdp-description__form__auto-delivery__popup">
                                                    Love this product? Sign up for our Auto-Delivery Program!{' '}
                                                    <ul className="dashed">
                                                        <li>Lock in the price</li>
                                                        <li>Get it on a schedule</li>
                                                        <li>Cancel at any time without risk</li>
                                                    </ul>
                                                    <PopoverClose />
                                                    <PopoverArrow className="popover__arrow" />
                                                </PopoverContent>
                                            </Popover>
                                        </b>

                                        <select
                                            className="pdp-description__form__auto-delivery__select rounded-none"
                                            aria-label="Product Selection"
                                            value={selectedADP}
                                            onChange={(e) => {
                                                const adpValue = parseInt(e.target.value, 10);
                                                setSelectedADP(adpValue);
                                            }}
                                        >
                                            {DeliveryOptions.map((deliveryOption) => (
                                                <option value={deliveryOption.EdpNo} key={`${deliveryOption.EdpNo}`}>
                                                    {deliveryOption.DisplayName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </fieldset>
                                <div className="horizontal-divider pdp-description__divider" />
                            </>
                        )}

                        <PdpSticky
                            ProductData={productData}
                            SelectedEdpItem={getCurrentEdpItem}
                            isAutoDeliveryEnabled={isAutoDeliveryEnabled}
                            showSticky={inView}
                            entry={entry}
                        >
                            <div className="pdp-sticky__layout">
                                <div className="pdp-sticky__name">
                                    <h3 className="pdp-description__product-name">
                                        <PdpName
                                            ProductData={productData}
                                            SelectedEdpItem={getCurrentEdpItem}
                                            isAutoDeliveryEnabled={isAutoDeliveryEnabled}
                                        />
                                    </h3>
                                </div>
                                <div className="pdp-sticky__prices">
                                    <PdpPrices ProductData={productData} SelectedEdpItem={getCurrentEdpItem} />
                                </div>
                                <div className="pdp-sticky__add-button">
                                    <button
                                        className="pdp-description__add-to-bag__add-to-bag-button"
                                        disabled={isCtaDisabled}
                                        type="button"
                                        onClick={onAddToBagClick}
                                    >
                                        {isAddedToBag && (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="16"
                                                viewBox="0 0 20 16"
                                                className="pdp-description__add-to-bag__add-to-bag-button__added-icon"
                                            >
                                                <g
                                                    fill="none"
                                                    fillRule="evenodd"
                                                    stroke="none"
                                                    strokeLinecap="round"
                                                    strokeWidth="1"
                                                >
                                                    <g
                                                        className="chevron chevron-stroke--white"
                                                        strokeWidth="2"
                                                        transform="translate(-85 -609)"
                                                    >
                                                        <g transform="translate(13 591)">
                                                            <path d="M73 26L79.5454545 32 91 19"></path>
                                                        </g>
                                                    </g>
                                                </g>
                                            </svg>
                                        )}

                                        <div
                                            className="pdp-description__add-to-bag__add-to-bag-button__loading"
                                            style={{ display: isAddToBagLoading ? 'flex' : 'none' }}
                                        >
                                            <img
                                                src={LOADING_ASSET_PATH}
                                                alt=""
                                                className="pdp-description__add-to-bag__add-to-bag-button__loading-icon"
                                            />
                                        </div>

                                        <span>{buttonText}</span>
                                    </button>
                                </div>

                                <div className="pdp-sticky__favorite">
                                    <div className="pdp-description__add-to-bag__favorite">
                                        <span className="pdp-description__add-to-bag__favorite__text">
                                            {isFavourite ? t('addedToFavourite') : t('addToFavourite')}
                                        </span>
                                        <button
                                            className="pdp-description__add-to-bag__favorite__icon-wrapper"
                                            type="button"
                                            aria-label="Add to Favorite"
                                            onClick={onFavoriteClick}
                                        >
                                            {isFavourite ? (
                                                <FavoriteIcon className="pdp-description__add-to-bag__favorite__icon-self" />
                                            ) : (
                                                <FavoriteEmptyIcon className="pdp-description__add-to-bag__favorite__icon-self" />
                                            )}
                                        </button>
                                        {showFavoriteTooltip && (
                                            <>
                                                <div
                                                    className="pdp-description__add-to-bag__favorite__overlay"
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={(e) => onFavoriteTooltipCloseClick(e)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            onFavoriteTooltipCloseClick(e);
                                                        }
                                                    }}
                                                    aria-label={t('closeText')}
                                                ></div>
                                                <div className="pdp-description__add-to-bag__favorite__tooltip">
                                                    <div className="pdp-description__add-to-bag__favorite__tooltip-content">
                                                        <span className="pdp-description__add-to-bag__favorite__tooltip-content__text">
                                                            {t('please') + ' '}
                                                            <a
                                                                className="pdp-description__add-to-bag__favorite__tooltip__sign-in"
                                                                href="/pages/signin"
                                                                key={1}
                                                                onClick={(e) => {
                                                                    handleSignInLink(e);
                                                                }}
                                                            >
                                                                <b>{t('signIn')}</b>
                                                            </a>
                                                            /
                                                            <a
                                                                className="pdp-description__add-to-bag__favorite__tooltip__register"
                                                                href="/pages/createaccount"
                                                                key={2}
                                                            >
                                                                <b>{t('register')}</b>
                                                            </a>
                                                            {' ' + t('toAddItemsToYourFavoritesList')}
                                                        </span>
                                                        <button
                                                            onClick={(e) => onFavoriteTooltipCloseClick(e)}
                                                            className="pdp-description__add-to-bag__favorite__tooltip-content__close-button"
                                                        >
                                                            <span className="visually-hidden">{t('closeText')}</span>
                                                            <TooltipClose />
                                                        </button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </PdpSticky>

                        {productData.EnabledAddToCart && (
                            <div className="pdp-description__add-to-bag">
                                <div className="pdp-description__add-to-bag__quantity">
                                    <span>
                                        <span className="visually-hidden">{t('quantity')}</span>
                                        <span
                                            className="pdp-description__add-to-bag__quantity__self"
                                            style={{ opacity: isOutOfStock ? 0.5 : 1 }}
                                        >
                                            {t('quantity')}:
                                        </span>
                                        {isCriticStock && (
                                            <>
                                                <span className="pdp-description__add-to-bag__quantity__count--critic-stock">
                                                    {critickStockText}
                                                </span>
                                            </>
                                        )}
                                        {isOutOfStock && !isPermanentlyOutOfStock && (
                                            <div className="pdp-description__out-of-stock">
                                                <span className="pdp-description__out-of-stock__text">
                                                    {t('outOfStockText')}
                                                </span>
                                            </div>
                                        )}
                                    </span>
                                    <select
                                        className="pdp-description__add-to-bag__quantity__select rounded-none"
                                        disabled={allowedQuantites[0] === 0}
                                        aria-label="Quantity"
                                        value={selectedQuantity}
                                        onChange={(e) => {
                                            const qty = parseInt(e.target.value, 10) || 0;
                                            setSelectedQuantity(qty);
                                        }}
                                    >
                                        {allowedQuantites.map((qty) => (
                                            <option value={qty} key={qty}>
                                                {qty}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    className="pdp-description__add-to-bag__add-to-bag-button"
                                    disabled={isCtaDisabled}
                                    type="button"
                                    onClick={onAddToBagClick}
                                    ref={ref}
                                >
                                    {isAddedToBag && (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="16"
                                            viewBox="0 0 20 16"
                                            className="pdp-description__add-to-bag__add-to-bag-button__added-icon"
                                        >
                                            <g
                                                fill="none"
                                                fillRule="evenodd"
                                                stroke="none"
                                                strokeLinecap="round"
                                                strokeWidth="1"
                                            >
                                                <g
                                                    className="chevron chevron-stroke--white"
                                                    strokeWidth="2"
                                                    transform="translate(-85 -609)"
                                                >
                                                    <g transform="translate(13 591)">
                                                        <path d="M73 26L79.5454545 32 91 19"></path>
                                                    </g>
                                                </g>
                                            </g>
                                        </svg>
                                    )}

                                    <div
                                        className="pdp-description__add-to-bag__add-to-bag-button__loading"
                                        style={{ display: isAddToBagLoading ? 'flex' : 'none' }}
                                    >
                                        <img
                                            src={LOADING_ASSET_PATH}
                                            alt=""
                                            className="pdp-description__add-to-bag__add-to-bag-button__loading-icon"
                                        />
                                    </div>

                                    <span>{buttonText}</span>
                                </button>
                                <AddToBag />
                                <div className="pdp-description__add-to-bag__favorite">
                                    <span className="pdp-description__add-to-bag__favorite__text">
                                        {isFavourite ? t('addedToFavourite') : t('addToFavourite')}
                                    </span>
                                    <button
                                        className="pdp-description__add-to-bag__favorite__icon-wrapper"
                                        type="button"
                                        aria-label="Add to Favorite"
                                        onClick={onFavoriteClick}
                                    >
                                        {isFavourite ? (
                                            <FavoriteIcon className="pdp-description__add-to-bag__favorite__icon-self" />
                                        ) : (
                                            <FavoriteEmptyIcon className="pdp-description__add-to-bag__favorite__icon-self" />
                                        )}
                                    </button>
                                    {showFavoriteTooltip && (
                                        <>
                                            <div
                                                className="pdp-description__add-to-bag__favorite__overlay"
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        onFavoriteTooltipCloseClick(e);
                                                    }
                                                }}
                                                aria-label={t('closeText')}
                                                onClick={(e) => onFavoriteTooltipCloseClick(e)}
                                            ></div>
                                            <div className="pdp-description__add-to-bag__favorite__tooltip">
                                                <div className="pdp-description__add-to-bag__favorite__tooltip-content">
                                                    <span className="pdp-description__add-to-bag__favorite__tooltip-content__text">
                                                        {t('please') + ' '}
                                                        <a
                                                            className="pdp-description__add-to-bag__favorite__tooltip__sign-in"
                                                            href="/pages/signin"
                                                            key={1}
                                                            onClick={(e) => {
                                                                handleSignInLink(e);
                                                            }}
                                                        >
                                                            <b>{t('signIn')}</b>
                                                        </a>
                                                        /
                                                        <a
                                                            className="pdp-description__add-to-bag__favorite__tooltip__register"
                                                            href="/pages/createaccount"
                                                            key={2}
                                                        >
                                                            <b>{t('register')}</b>
                                                        </a>
                                                        {' ' + t('toAddItemsToYourFavoritesList')}
                                                    </span>
                                                    <button
                                                        onClick={(e) => onFavoriteTooltipCloseClick(e)}
                                                        className="pdp-description__add-to-bag__favorite__tooltip-content__close-button"
                                                    >
                                                        <span className="visually-hidden">{t('closeText')}</span>
                                                        <TooltipClose />
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {SellerType.toString() === TmSellerTypeEnum.Marketplace && (
                            <div className="pdp-description-seller">
                                <div className="pdp-description-seller-marketplace">
                                    <img
                                        src="/App_Assets/public/marketplace-icon.svg"
                                        alt="marketplace-icon"
                                        className="pdp-description-seller-marketplace-icon"
                                    />
                                    <span className="pdp-description-seller-marketplace-title">
                                        {t('Sold and fulfilled by')}{' '}
                                        <a href={SellerPageLink} target="_blank" rel="noreferrer">
                                            <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                                {SellerName}
                                            </span>
                                        </a>
                                    </span>
                                </div>
                            </div>
                        )}

                        <PdpAdvanceOrder SelectedEdpItem={getCurrentEdpItem} />

                        {errorMessage != '' && (
                            <div className="pdp-description__error-message">
                                <span className="pdp-description__error-message__text">{errorMessage}</span>
                            </div>
                        )}

                        {showEasyPayPopup && (
                            <PdpEasyPayPopup
                                title={easyPayPopupData.title}
                                content={easyPayPopupData.content}
                                onCloseClick={() => {
                                    setShowEasyPayPopup(false);
                                }}
                            />
                        )}
                    </form>
                </>
            )}
        </>
    );
};

export default PdpDescriptionArea;
